/**
 * User Matrix — roles, users, column visibility per board (synced via API).
 */
import axios from "axios";
import { getApiBase } from "../apiBase";
import { readStoredAdminFlag } from "../authAdmin";
import { buildPluginsHeader } from "./registry";

export const USER_MATRIX_STORAGE_KEY = "pb.plugin.userMatrix.v1";
export const ADMINISTRATOR_TYPE_ID = "administrator";
export const COORDINATOR_TYPE_ID = "role_moiy1q1m_gof64a";
export const PERMITTING_MANAGER_TYPE_ID = "role_permit_mgr_pb01";
export const PERMITTING_SPECIALIST_TYPE_ID = "role_permit_spec_pb01";
export const ENGINEERING_MANAGER_TYPE_ID = "role_mokc6l2i_tpgjpy";
export const ENGINEERING_SUPERVISOR_TYPE_ID = "role_eng_supervisor_pb01";
export const DRAFTER_TYPE_ID = "role_mokc6r8s_5skrub";
export const OSP_ENGINEERING_TYPE_ID = "role_osp_eng_pb01";
export const INSPECTOR_TYPE_ID = "role_inspector_pb01";
export const CONTRACTOR_TYPE_ID = "role_contractor_pb01";
export const ACCOUNTANT_TYPE_ID = "role_accountant_pb01";
export const SPLICER_TYPE_ID = "role_splicer_pb01";
export const SPLICING_MANAGER_TYPE_ID = "role_mp4mvwug_yeiuu0";
export const NETWORK_TECH_TYPE_ID = "role_network_tech_pb01";
export const NETWORK_MANAGER_TYPE_ID = "role_moizkdfs_sese41";
export const SALES_MANAGER_TYPE_ID = "role_sales_mgr_pb01";

/** Roles with the same operational/edit access as Coordinator. */
export const COORDINATOR_EQUIVALENT_ROLE_NAMES = ["coordinator"];

const COORDINATOR_EQUIVALENT_TYPE_IDS = new Set([COORDINATOR_TYPE_ID]);

function normalizeMatrixRoleName(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function mergeRoleTypePermissionsPreferGrants(canonical, donor) {
  const perms = { ...(canonical.permissions || {}) };
  for (const [k, v] of Object.entries(donor?.permissions || {})) {
    if (v === true) perms[k] = true;
  }
  const dept = [
    ...new Set([
      ...(Array.isArray(canonical.departmentTags) ? canonical.departmentTags : []),
      ...(Array.isArray(donor?.departmentTags) ? donor.departmentTags : []),
    ]),
  ];
  return {
    ...canonical,
    permissions: perms,
    commercialColumnIds:
      canonical.commercialColumnIds != null ? canonical.commercialColumnIds : donor?.commercialColumnIds ?? null,
    expansionColumnIds:
      canonical.expansionColumnIds != null ? canonical.expansionColumnIds : donor?.expansionColumnIds ?? null,
    departmentTags: dept,
  };
}

function isInspectorRoleName(name) {
  const n = normalizeMatrixRoleName(name);
  return Boolean(n) && (n === "inspector" || n.includes("inspector"));
}

function isOspEngineerRoleName(name) {
  const n = normalizeMatrixRoleName(name);
  if (!n) return false;
  if (n === "osp engineer" || n === "osp engineering") return true;
  return n.includes("osp engineer");
}

function isSplicerRoleName(name) {
  return normalizeMatrixRoleName(name) === "splicer";
}

function isContractorRoleName(name) {
  return normalizeMatrixRoleName(name) === "contractor";
}

function unionColumnIdLists(a, b) {
  if (a == null || b == null) return null;
  const arr = [
    ...new Set(
      [...(Array.isArray(a) ? a : []), ...(Array.isArray(b) ? b : [])]
        .map((x) => String(x || "").trim())
        .filter(Boolean)
    ),
  ];
  return arr.length === 0 ? [] : arr;
}

/** Fold duplicate Inspector / OSP Engineer / Splicer role types onto canonical ids (mirrors backend). */
function consolidateCanonicalRoleTypes(matrix) {
  let userTypes = Array.isArray(matrix?.userTypes) ? matrix.userTypes.map((t) => ({ ...t })) : [];
  const users = Array.isArray(matrix?.users) ? matrix.users.map((u) => ({ ...u })) : [];
  const pendingInvites = Array.isArray(matrix?.pendingInvites)
    ? matrix.pendingInvites.map((p) => ({ ...p }))
    : [];

  const fold = (matchFn, canonId, canonName, { unionColumns = false } = {}) => {
    const duplicates = userTypes.filter((t) => matchFn(t.name) && t.id !== canonId);
    for (const dup of duplicates) {
      for (const u of users) {
        if (u.userTypeId === dup.id) u.userTypeId = canonId;
      }
      for (const p of pendingInvites) {
        if (p.userTypeId === dup.id) p.userTypeId = canonId;
      }
      userTypes = userTypes.filter((t) => t.id !== dup.id);
      const existingIdx = userTypes.findIndex((t) => t.id === canonId);
      if (existingIdx < 0) {
        userTypes.push({ ...dup, id: canonId, name: canonName });
      } else {
        const kept = userTypes[existingIdx];
        const merged = mergeRoleTypePermissionsPreferGrants(kept, dup);
        if (unionColumns) {
          merged.commercialColumnIds = unionColumnIdLists(
            kept.commercialColumnIds,
            dup.commercialColumnIds
          );
          merged.expansionColumnIds = unionColumnIdLists(
            kept.expansionColumnIds,
            dup.expansionColumnIds
          );
        }
        merged.name = canonName;
        userTypes[existingIdx] = merged;
      }
    }
    for (const t of userTypes) {
      if (t.id === canonId || matchFn(t.name)) t.name = canonName;
    }
  };

  fold(isInspectorRoleName, INSPECTOR_TYPE_ID, "Inspector");
  fold(isOspEngineerRoleName, OSP_ENGINEERING_TYPE_ID, "OSP Engineer");
  fold(isSplicerRoleName, SPLICER_TYPE_ID, "Splicer", { unionColumns: true });
  fold(isContractorRoleName, CONTRACTOR_TYPE_ID, "Contractor");

  return { userTypes, users, pendingInvites };
}

export function isCoordinatorEquivalentRoleName(name) {
  const n = normalizeMatrixRoleName(name);
  if (!n) return false;
  return COORDINATOR_EQUIVALENT_ROLE_NAMES.some((label) => n === label || n.includes(label));
}

const SETTINGS_PERMISSION_KEYS = new Set(["canManagePlugins", "canManageUserMatrix"]);
export const AUTH_USER_EMAIL_KEY = "projectBoardAuthUserEmail";
export const IMPERSONATION_STORAGE_KEY = "pb.userImpersonation.v1";
export const SHARED_MAILBOX_DOMAINS = ["cvin.com", "vastnetworks.com"];

export const DEPARTMENT_OPTIONS = [
  { id: "sales", label: "Sales" },
  { id: "finance", label: "Finance" },
  { id: "engineering", label: "Engineering" },
  { id: "permitting", label: "Permitting" },
  { id: "construction", label: "Construction" },
  { id: "fiber", label: "Fiber" },
  { id: "networking", label: "Networking" },
  { id: "accounting", label: "Accounting" },
];

/** Commercial grid column department rules (All columns mode). */
const COLUMN_DEPARTMENT_POLICY = {
  id: "none",
  legacy_wp_post_id: "none",
  work_order: "all",
  work_order_base: "none",
  name: "none",
  export_slug: "none",
  customer: "all",
  status: "all",
  phase: "all",
  active_subphase: "all",
  project_type: "all",
  subtype: "all",
  department: "all",
  site_name: "none",
  service_location: "all",
  sales_rep: "all",
  mrc: { departments: ["sales", "finance", "networking", "accounting"] },
  non_recurring_charge: { departments: ["sales", "finance", "accounting"] },
  term_in_months: { departments: ["sales", "finance", "accounting"] },
  invoiced_date: { departments: ["accounting"] },
  circuit_id: { departments: ["networking", "fiber"] },
  cut_sheet: { departments: ["networking"] },
  nid_config: { departments: ["networking"] },
  topology: { departments: ["networking"] },
  preliminary_route: { departments: ["engineering", "permitting"] },
  construction_footage: { departments: ["engineering", "construction"] },
  permit_files: { departments: ["engineering", "permitting"] },
  permit_received_date: { departments: ["engineering", "permitting"] },
  permit_expiration_date: { departments: ["engineering", "permitting"] },
  refundable_deposit: { departments: ["engineering", "permitting"] },
  deposit_amount: { departments: ["engineering", "permitting"] },
  materials_summary: { departments: ["engineering", "permitting"] },
  materials_review: { departments: ["engineering", "permitting"] },
  usa_tickets: { departments: ["engineering", "permitting", "construction"] },
  asbuilts: { departments: ["engineering", "construction"] },
  drafter: { departments: ["engineering"] },
  permitting_specialist: { departments: ["permitting"] },
  splicer: { departments: ["fiber"] },
  assigned_network_tech: { departments: ["networking"] },
  bid_out: { departments: ["construction"] },
  contractor: { departments: ["construction"] },
  contractor_fiber: { departments: ["construction", "fiber"] },
};

/** Expansion grid: subsegment_summary + domain groups (All columns mode). */
const EXPANSION_COLUMN_DEPARTMENT_POLICY = {
  work_order: "all",
  exp_segment: "all",
  exp_project_name: "all",
  exp_status: "all",
  phase: "all",
  active_subphase: "all",
  exp_departments: "all",
  exp_bid_out: "all",
  exp_construction_footage: "all",
  exp_override_footage: "all",
  exp_caltrans_footage: "all",
  exp_handholes: "all",
  exp_completed_conduit_footage: "all",
  exp_completed_fiber_footage: "all",
  exp_route: { departments: ["engineering", "permitting"] },
  exp_drafter: { departments: ["engineering"] },
  exp_permitting_specialist: { departments: ["permitting"] },
  exp_permits: { departments: ["engineering", "permitting"] },
  exp_permit_received_date: { departments: ["engineering", "permitting"] },
  exp_permit_expiration_date: { departments: ["engineering", "permitting"] },
  exp_refundable_deposit: { departments: ["engineering", "permitting"] },
  exp_deposit_amount: { departments: ["engineering", "permitting"] },
  exp_materials_summary: { departments: ["engineering", "permitting"] },
  exp_materials_review: { departments: ["engineering", "permitting"] },
  exp_inspector: { departments: ["construction"] },
  exp_construction_contractor: { departments: ["construction"] },
  exp_fiber_contractor: { departments: ["construction", "fiber"] },
  exp_asbuilt: { departments: ["construction", "engineering"] },
  exp_usa_ticket: { departments: ["construction", "engineering"] },
  exp_splicer: { departments: ["fiber"] },
  exp_fiber_otdr: { departments: ["fiber"] },
  exp_completion_notice: { departments: ["accounting"] },
  exp_invoiced: { departments: ["accounting"] },
};

let userMatrixCache = null;
let viewerSessionProfile = null;

function splitEmail(email) {
  const e = String(email || "").trim().toLowerCase();
  const at = e.lastIndexOf("@");
  if (at <= 0) return { local: "", domain: "", normalized: e };
  return { local: e.slice(0, at), domain: e.slice(at + 1), normalized: e };
}

export function sameOrgMailbox(a, b) {
  const ea = splitEmail(a);
  const eb = splitEmail(b);
  if (!ea.local || !eb.local) return false;
  if (ea.local !== eb.local) return false;
  if (SHARED_MAILBOX_DOMAINS.includes(ea.domain) && SHARED_MAILBOX_DOMAINS.includes(eb.domain)) return true;
  return ea.normalized === eb.normalized;
}

export function findMatrixUser(users, email) {
  const em = String(email || "").trim().toLowerCase();
  if (!em || !Array.isArray(users)) return null;
  return users.find((u) => sameOrgMailbox(u.email, em)) || null;
}

function normalizeDepartmentTags(tags) {
  const allowed = new Set(DEPARTMENT_OPTIONS.map((d) => d.id));
  if (!Array.isArray(tags)) return [];
  return [...new Set(tags.map((t) => String(t || "").trim().toLowerCase()).filter((t) => allowed.has(t)))];
}


const EXPANSION_COLUMN_ID_MIGRATION = {
  name: "exp_project_name",
  status: "exp_status",
};

function migrateExpansionColumnIds(ids) {
  if (ids === null || ids === undefined) return ids;
  if (!Array.isArray(ids)) return ids;
  const known = new Set(listKnownExpansionColumnIds());
  const out = [];
  for (const raw of ids) {
    const id = String(raw || "").trim();
    if (!id) continue;
    const mapped = EXPANSION_COLUMN_ID_MIGRATION[id] || id;
    if (known.has(mapped)) out.push(mapped);
  }
  return [...new Set(out)];
}

function normalizeSettings(raw) {
  const base = {
    userTypes: [{ id: ADMINISTRATOR_TYPE_ID, name: "Administrator", permissions: {}, commercialColumnIds: null, expansionColumnIds: null, departmentTags: [] }],
    users: [],
    pendingInvites: [],
  };
  if (!raw || typeof raw !== "object") return base;
  const consolidated = consolidateCanonicalRoleTypes({
    userTypes: Array.isArray(raw.userTypes) ? raw.userTypes : base.userTypes,
    users: Array.isArray(raw.users) ? raw.users : [],
    pendingInvites: Array.isArray(raw.pendingInvites) ? raw.pendingInvites : [],
  });
  const migratedTypes = consolidated.userTypes.map((t) => {
    if (t.expansionColumnIds === null || t.expansionColumnIds === undefined) return t;
    return { ...t, expansionColumnIds: migrateExpansionColumnIds(t.expansionColumnIds) };
  });
  const users = consolidated.users.map((u) => ({
    ...u,
    contractorName:
      u.contractorName != null && String(u.contractorName).trim() !== ""
        ? String(u.contractorName).trim()
        : "",
  }));
  return {
    userTypes: migratedTypes,
    users,
    pendingInvites: consolidated.pendingInvites,
  };
}

export function loadUserMatrixSettings() {
  if (userMatrixCache) return userMatrixCache;
  try {
    const raw = localStorage.getItem(USER_MATRIX_STORAGE_KEY);
    if (raw) {
      userMatrixCache = normalizeSettings(JSON.parse(raw));
      return userMatrixCache;
    }
  } catch {
    /* ignore */
  }
  userMatrixCache = normalizeSettings(null);
  return userMatrixCache;
}

export function hydrateUserMatrixFromServer(matrix) {
  userMatrixCache = normalizeSettings(matrix);
  try {
    localStorage.setItem(USER_MATRIX_STORAGE_KEY, JSON.stringify(userMatrixCache));
    window.dispatchEvent(new Event("pb-user-matrix-settings-changed"));
  } catch {
    /* ignore */
  }
}

export function clearUserMatrixHydration() {
  userMatrixCache = null;
  viewerSessionProfile = null;
}

export function hydrateViewerSessionFromServer(profile) {
  viewerSessionProfile = profile && typeof profile === "object" ? profile : null;
}

export async function saveUserMatrixSettings(settings) {
  const next = normalizeSettings(settings);
  const { data } = await axios.put(
    `${getApiBase()}/admin/app-config`,
    { userMatrix: next },
    { headers: matrixAuthHeaders() }
  );
  if (data?.userMatrix) hydrateUserMatrixFromServer(data.userMatrix);
  else hydrateUserMatrixFromServer(next);
  return loadUserMatrixSettings();
}

function matrixAuthHeaders() {
  const h = { ...buildPluginsHeader() };
  try {
    const token = localStorage.getItem("projectBoardAuthToken");
    if (token) h.Authorization = `Bearer ${token}`;
  } catch {
    /* ignore */
  }
  return h;
}

export function getViewerEmailFromStorage() {
  try {
    const imp = getImpersonation();
    if (isImpersonating() && imp?.email) {
      return String(imp.email).trim().toLowerCase();
    }
  } catch {
    /* ignore */
  }
  if (viewerSessionProfile?.email) return String(viewerSessionProfile.email).trim().toLowerCase();
  try {
    return String(localStorage.getItem(AUTH_USER_EMAIL_KEY) || "").trim().toLowerCase();
  } catch {
    return "";
  }
}

function resolveViewerRoleType(viewerEmail) {
  const s = loadUserMatrixSettings();
  try {
    const imp = getImpersonation();
    if (isImpersonating() && imp?.userTypeId) {
      return s.userTypes.find((t) => t.id === imp.userTypeId) || null;
    }
  } catch {
    /* ignore */
  }
  const email = String(viewerEmail || getViewerEmailFromStorage() || "").trim().toLowerCase();
  if (!email) return null;
  const user = findMatrixUser(s.users, email);
  if (!user) return null;
  return s.userTypes.find((t) => t.id === user.userTypeId) || null;
}

export function getViewerRoleType(viewerEmail) {
  return resolveViewerRoleType(viewerEmail);
}

/** True when the signed-in user is admin but not previewing another role. */
export function hasEffectiveAdminPrivileges() {
  return readStoredAdminFlag() && !isImpersonating();
}

export function isViewerAdministrator(viewerEmail) {
  if (!isImpersonating() && viewerSessionProfile?.admin === true) return true;
  const t = resolveViewerRoleType(viewerEmail);
  return t?.id === ADMINISTRATOR_TYPE_ID;
}

export function isRoleTypeCoordinator(roleType) {
  if (!roleType) return false;
  if (COORDINATOR_EQUIVALENT_TYPE_IDS.has(String(roleType.id || ""))) return true;
  return isCoordinatorEquivalentRoleName(roleType.name);
}

export function isRoleTypeOperationalAdmin(roleType) {
  if (!roleType) return false;
  if (roleType.id === ADMINISTRATOR_TYPE_ID) return true;
  return isRoleTypeCoordinator(roleType);
}

export function isViewerCoordinator(viewerEmail) {
  return isRoleTypeCoordinator(resolveViewerRoleType(viewerEmail));
}

/** Full operational access (admin matrix role or coordinator), excluding Settings-only gates. */
export function hasViewerOperationalAdminAccess(viewerEmail) {
  if (isViewerAdministrator(viewerEmail)) return true;
  return isViewerCoordinator(viewerEmail);
}

/** User Matrix role id for the signed-in (or impersonated) viewer. */
export function getViewerUserTypeId(viewerEmail) {
  return resolveViewerRoleType(viewerEmail)?.id || null;
}

export function canViewerPermission(viewerEmail, key, defaultWhenUnknown = false) {
  if (isViewerAdministrator(viewerEmail)) return true;
  if (isViewerCoordinator(viewerEmail)) {
    if (SETTINGS_PERMISSION_KEYS.has(key)) return false;
    return true;
  }
  const t = resolveViewerRoleType(viewerEmail);
  if (!t?.permissions || typeof t.permissions !== "object") return defaultWhenUnknown;
  if (t.permissions[key] === undefined) return defaultWhenUnknown;
  return Boolean(t.permissions[key]);
}

export function canViewerAccessPanel(viewerEmail, panel, pluginEnabled) {
  if (!pluginEnabled) return true;
  const key = panel === "expansion" ? "canAccessExpansion" : "canAccessCommercial";
  return canViewerPermission(viewerEmail, key, true);
}

function groupedColumnPolicyForRole(roleType, panel) {
  const dept = new Set(normalizeDepartmentTags(roleType?.departmentTags));
  const policyMap = panel === "expansion" ? EXPANSION_COLUMN_DEPARTMENT_POLICY : COLUMN_DEPARTMENT_POLICY;
  const forcedVisible = new Set();
  const forcedHidden = new Set();
  for (const [colId, policy] of Object.entries(policyMap)) {
    if (policy === "all") {
      forcedVisible.add(colId);
      continue;
    }
    if (policy === "none") {
      forcedHidden.add(colId);
      continue;
    }
    if (policy && typeof policy === "object" && Array.isArray(policy.departments)) {
      const applies = policy.departments.some((d) => dept.has(d));
      if (applies) forcedVisible.add(colId);
      else forcedHidden.add(colId);
    }
  }
  return { forcedVisible, forcedHidden };
}

export function getAllowedColumnIdsForViewer(viewerEmail, panel) {
  const t = resolveViewerRoleType(viewerEmail);
  if (!t) return null;
  const ids = panel === "expansion" ? t.expansionColumnIds : t.commercialColumnIds;
  if (ids === null || ids === undefined) return null;
  return new Set((Array.isArray(ids) ? ids : []).map((x) => String(x || "").trim()).filter(Boolean));
}

export function applyUserMatrixColumnVisibility(defs, panel, viewerEmail, pluginEnabled) {
  if (!pluginEnabled) return defs;
  const allowed = getAllowedColumnIdsForViewer(viewerEmail, panel);
  const viewerType = resolveViewerRoleType(viewerEmail);
  const { forcedVisible, forcedHidden } = groupedColumnPolicyForRole(viewerType, panel);
  return defs.map((col) => {
    const id = col.colId ?? col.field;
    if (!id || id === "__select__" || id === "__expansion_group__") return col;
    const key = String(id);
    if (allowed !== null) {
      return { ...col, hide: !allowed.has(key) };
    }
    if (forcedVisible.has(key)) return { ...col, hide: false };
    if (forcedHidden.has(key)) return { ...col, hide: true };
    return { ...col, hide: false };
  });
}

export function getImpersonation() {
  try {
    const raw = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function isImpersonating() {
  return Boolean(getImpersonation()?.userTypeId);
}

export function startImpersonation(payload) {
  try {
    localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(payload));
    window.dispatchEvent(new Event("pb-user-matrix-settings-changed"));
    window.dispatchEvent(new Event("pb-impersonation-changed"));
  } catch {
    /* ignore */
  }
}

export function stopImpersonation() {
  try {
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    window.dispatchEvent(new Event("pb-user-matrix-settings-changed"));
    window.dispatchEvent(new Event("pb-impersonation-changed"));
  } catch {
    /* ignore */
  }
}

function randomUrlToken(byteLength = 24) {
  const bytes = new Uint8Array(byteLength);
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  let bin = "";
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]);
  const b64 =
    typeof btoa === "function"
      ? btoa(bin)
      : Buffer.from(bytes).toString("base64");
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export function createInviteRecord(settings, email, userTypeId) {
  const em = String(email || "").trim().toLowerCase();
  if (!em || !userTypeId) {
    return { settings, inviteUrl: null, token: null, error: "Email and role required." };
  }
  if (settings.users.some((u) => sameOrgMailbox(u.email, em))) {
    return { settings, inviteUrl: null, token: null, error: "That email is already in the matrix." };
  }
  const token = `inv_${randomUrlToken(24)}`;
  const userId = `u_${randomUrlToken(12)}`;
  const pendingInvites = [
    ...(settings.pendingInvites || []).filter((p) => !sameOrgMailbox(p.email, em)),
    {
      token,
      email: em,
      userTypeId: String(userTypeId),
      createdAt: Date.now(),
    },
  ];
  const next = {
    ...settings,
    users: [
      ...settings.users,
      { id: userId, email: em, userTypeId: String(userTypeId), status: "pending" },
    ],
    pendingInvites,
  };
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const inviteUrl = `${origin}/?invite=${encodeURIComponent(token)}&auth=signup`;
  return { settings: next, inviteUrl, token, error: null };
}

export async function markInviteEmailSent(token) {
  await markInviteEmailsSent([token]);
}

/** Mark multiple invite tokens as emailed in one save. */
export async function markInviteEmailsSent(tokens) {
  const tokenSet = new Set(
    (Array.isArray(tokens) ? tokens : [])
      .map((t) => String(t || "").trim())
      .filter(Boolean)
  );
  if (!tokenSet.size) return;
  const s = loadUserMatrixSettings();
  const pendingInvites = (s.pendingInvites || []).map((p) =>
    tokenSet.has(String(p.token || "")) ? { ...p, inviteEmailSentAt: Date.now() } : p
  );
  await saveUserMatrixSettings({ ...s, pendingInvites });
}

function roleNameMatchesEngineeringCatalog(roleType) {
  if (!roleType) return false;
  if (isRoleTypeOperationalAdmin(roleType)) return true;
  const id = String(roleType.id || "");
  if (id === ENGINEERING_MANAGER_TYPE_ID || id === ENGINEERING_SUPERVISOR_TYPE_ID || id === DRAFTER_TYPE_ID) {
    return true;
  }
  const name = normalizeMatrixRoleName(roleType.name);
  if (name === "drafter") return true;
  if (name.includes("engineering supervisor") || (name.includes("engineering") && name.includes("supervisor"))) {
    return true;
  }
  if (name.includes("engineering manager")) return true;
  return name.includes("engineering") && (name.includes("supervisor") || name.includes("manager"));
}

export function canManageEngineeringMaterialsCatalog(viewerEmail) {
  return roleNameMatchesEngineeringCatalog(resolveViewerRoleType(viewerEmail));
}

export function canManageEngineeringDrawings(viewerEmail) {
  return canManageEngineeringMaterialsCatalog(viewerEmail);
}

/** Valid expansion column ids for migrating stale role whitelists. */
export function listKnownExpansionColumnIds() {
  const { getExpansionGridColumnDescriptors } = require("../projectGridColumns");
  return getExpansionGridColumnDescriptors().map((d) => d.colId);
}
